import React, { useState, useEffect } from "react";
import CustomModal from "../common/CustomModal";
import Button from "../common/Button";
import TextInput from "../common/TextInput";
import { useFormik } from "formik";
import {
  useAddBusiness,
  useGetBusinessCategories,
} from "../../api/services/businessServices";
import { errorToast, successToast } from "../../helpers/constants";
import { useQueryClient } from "@tanstack/react-query";
import FileUpload from "../common/FileUpload";
import { addBusinessValidationSchema } from "../../helpers/validationSchema";
import { AiOutlineCheck } from "react-icons/ai";
import { businessLogoImg } from "../../assets";
import { useSelector } from "react-redux";
import { FaSpinner, FaStore, FaClock, FaTag, FaImage, FaMapMarkerAlt } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

const AddBusinessModal = ({ isOpen, onClose }) => {
  const businessMutation = useAddBusiness();
  const queryClient = useQueryClient();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState([]);
  const [availableBusinessTypes, setAvailableBusinessTypes] = useState([]);
  const [preview, setPreview] = useState(businessLogoImg);

  const { user } = useSelector((state) => state.auth);

  const {
    handleChange,
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      merchantId: user?.id || "",
      businessName: "",
      businessAddress: "",
      businessCategoryIds: [],
      openingHourFrom: "",
      openingHourTo: "",
      businessTypes: [],
      businessLogo: null,
    },
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("merchantId", values.merchantId);
      formData.append("businessName", values.businessName);
      formData.append("businessAddress", values.businessAddress);
      formData.append(
        "openingHours",
        `${values.openingHourFrom} - ${values.openingHourTo}`
      );
      formData.append(
        "businessCategoryIds",
        JSON.stringify(selectedCategories.map((cat) => cat.id))
      );
      formData.append("businessTypes", JSON.stringify(selectedBusinessTypes));
      formData.append("logo", values.businessLogo);

      try {
        await businessMutation.mutateAsync(formData, {
          onError: (err) => {
            errorToast(err?.response?.data?.error || "Error creating business");
            console.log("Error creating business", err);
          },
          onSuccess: (data) => {
            successToast(data?.message);
            handleCloseModal();
            queryClient.invalidateQueries(["getMerchantBusinesses"]);
          },
        });
      } catch (error) {
        console.log("Error creating business", error);
      }
    },
    validationSchema: addBusinessValidationSchema,
  });

  const { data: categories } = useGetBusinessCategories();

  useEffect(() => {
    setFieldValue(
      "businessCategoryIds",
      selectedCategories.map((cat) => cat.id)
    );
  }, [selectedCategories, setFieldValue]);

  const handleCloseModal = () => {
    onClose();
    setSelectedCategories([]);
    setSelectedBusinessTypes([]);
    setAvailableBusinessTypes([]);
    resetForm();
    setPreview(businessLogoImg);
  };

  const handleCategoryToggle = (category) => {
    const isSelected = selectedCategories.some((cat) => cat.id === category.id);
    let newSelectedCategories;

    if (isSelected) {
      newSelectedCategories = selectedCategories.filter(
        (cat) => cat.id !== category.id
      );
    } else {
      newSelectedCategories = [...selectedCategories, category];
    }

    setSelectedCategories(newSelectedCategories);

    const newBusinessTypes = newSelectedCategories.flatMap(
      (cat) => cat.businessTypes || []
    );

    const uniqueBusinessTypes = newBusinessTypes.filter(
      (type, index, arr) => arr.findIndex((t) => t.id === type.id) === index
    );

    setAvailableBusinessTypes(uniqueBusinessTypes);

    setSelectedBusinessTypes((prev) =>
      prev.filter((typeId) =>
        uniqueBusinessTypes.some((type) => type.id === typeId)
      )
    );
  };

  const handleBusinessTypeToggle = (typeId) => {
    setSelectedBusinessTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId]
    );
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={
        <div className="flex flex-row items-center gap-2 sm:gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
            <FaStore className="text-white text-lg" />
          </div>
          <div className="text-left">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Create New Business</h3>
            <p className="text-xs sm:text-sm text-gray-500">Set up your business profile and start growing</p>
          </div>
        </div>
      }
    >
      <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 p-4 sm:p-6 rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Business Logo Section */}
          <div className="text-center">
            <div className="inline-block relative">
              <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-3 sm:mb-4 relative">
                <img
                  src={preview}
                  alt="Business logo"
                  className="rounded-2xl w-full h-full object-cover shadow-lg border-4 border-white"
                />
                <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3 w-7 sm:w-8 h-7 sm:h-8 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
                  <FaImage className="text-white text-sm" />
                </div>
              </div>
              <FileUpload
                className="w-full max-w-xs mx-auto text-xs sm:text-sm"
                containerClasses="mb-0"
                placeholder="Upload Logo"
                id="businessLogo"
                accept="image/*"
                onChange={(event) => {
                  const file = event.currentTarget.files[0];
                  if (file) {
                    setFieldValue("businessLogo", file);
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <FaStore className="text-primary text-base sm:text-lg flex-shrink-0" />
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">Basic Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <TextInput
                  className="w-full p-2 sm:p-2.5 text-sm"
                  label="Business Name"
                  placeholder="e.g., ABC Restaurant Ltd."
                  type="text"
                  id="businessName"
                  value={values.businessName}
                  onChange={handleChange}
                  error={touched.businessName && errors.businessName}
                />
              </div>
              <div>
                <TextInput
                  className="w-full p-2 sm:p-2.5 text-sm"
                  label="Business Address"
                  placeholder="e.g., 15 Ecowas Avenue, Banjul"
                  type="text"
                  id="businessAddress"
                  value={values.businessAddress}
                  onChange={handleChange}
                  error={touched.businessAddress && errors.businessAddress}
                />
              </div>
            </div>
          </div>

          {/* Operating Hours Section */}
          <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <FaClock className="text-primary text-base sm:text-lg flex-shrink-0" />
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">Operating Hours</h4>
            </div>
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Opening Time
                  </label>
                  <input
                    type="time"
                    id="openingHourFrom"
                    name="openingHourFrom"
                    value={values.openingHourFrom}
                    onChange={handleChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm border ${
                      touched.openingHourFrom && errors.openingHourFrom
                        ? "border-red-400 bg-red-50"
                        : "border-gray-300 bg-white"
                    } focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all duration-200`}
                  />
                  {touched.openingHourFrom && errors.openingHourFrom && (
                    <span className="text-xs text-red-500 mt-1 block">
                      {errors.openingHourFrom}
                    </span>
                  )}
                </div>
                
                <div className="hidden sm:flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-sm flex-shrink-0">
                  <span className="text-lg font-bold text-primary">→</span>
                </div>
                <div className="sm:hidden text-center text-xs text-gray-500">to</div>
                
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Closing Time
                  </label>
                  <input
                    type="time"
                    id="openingHourTo"
                    name="openingHourTo"
                    value={values.openingHourTo}
                    onChange={handleChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm border ${
                      touched.openingHourTo && errors.openingHourTo
                        ? "border-red-400 bg-red-50"
                        : "border-gray-300 bg-white"
                    } focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all duration-200`}
                  />
                  {touched.openingHourTo && errors.openingHourTo && (
                    <span className="text-xs text-red-500 mt-1 block">
                      {errors.openingHourTo}
                    </span>
                  )}
                </div>
              </div>
              {errors.isValidRange && (
                <div className="mt-3 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-xs sm:text-sm text-red-600">{errors.isValidRange}</span>
                </div>
              )}
            </div>
          </div>

          {/* Business Categories Section */}
          <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-3 sm:mb-4">
              <FaTag className="text-primary text-base sm:text-lg flex-shrink-0" />
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">Business Categories</h4>
              <span className="hidden lg:inline text-xs text-gray-500">Choose categories</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2 mb-4 max-h-48 sm:max-h-56 overflow-y-auto p-1 sm:p-2">
              {categories?.data?.map((category) => {
                const isSelected = selectedCategories.some(
                  (cat) => cat.id === category.id
                );
                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryToggle(category)}
                    className={`relative p-2 sm:p-3 rounded-lg cursor-pointer border-2 transition-all duration-300 hover:shadow-lg hover:scale-100 sm:hover:scale-105 ${
                      isSelected
                        ? "border-primary bg-gradient-to-r from-primary to-secondary text-white shadow-lg"
                        : "border-gray-200 bg-white hover:border-primary/50 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        <h5 className={`font-semibold text-xs sm:text-sm truncate ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                          {category.categoryName}
                        </h5>
                        <p className={`text-xs mt-1 ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                          {category.businessTypes?.length || 0} types
                        </p>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                          <AiOutlineCheck className="text-white text-xs" />
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg"></div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {selectedCategories.length > 0 && (
              <div className="bg-primary/10 rounded-lg p-2 sm:p-3">
                <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Selected:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCategories.map((category) => (
                    <span
                      key={category.id}
                      className="inline-flex items-center px-2 sm:px-3 py-1 bg-gradient-to-r from-primary to-secondary text-white text-xs rounded-full shadow-sm"
                    >
                      {category.categoryName}
                      <HiSparkles className="ml-1 text-xs" />
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {touched.businessCategoryIds && errors.businessCategoryIds && (
              <div className="mt-3 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-xs sm:text-sm text-red-600">{errors.businessCategoryIds}</span>
              </div>
            )}
          </div>

          {/* Business Types Section */}
          <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-3 sm:mb-4">
              <FaTag className="text-secondary text-base sm:text-lg flex-shrink-0" />
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">Business Types</h4>
              {selectedCategories.length === 0 && (
                <span className="text-xs text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                  Select categories first
                </span>
              )}
            </div>
            
            {availableBusinessTypes.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4 max-h-40 sm:max-h-48 overflow-y-auto p-1 sm:p-2">
                  {availableBusinessTypes.map((type) => {
                    const isSelected = selectedBusinessTypes.includes(type.id);
                    return (
                      <div
                        key={type.id}
                        onClick={() => handleBusinessTypeToggle(type.id)}
                        className={`p-2 rounded-lg cursor-pointer border-2 transition-all duration-300 text-center hover:shadow-md hover:scale-100 sm:hover:scale-105 ${
                          isSelected
                            ? "border-secondary bg-secondary text-white shadow-lg"
                            : "border-gray-200 bg-white hover:border-secondary/50 text-gray-700"
                        }`}
                      >
                        <div className="text-xs sm:text-sm font-medium truncate">{type.name}</div>
                        {isSelected && (
                          <AiOutlineCheck className="mx-auto mt-1 text-xs" />
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {selectedBusinessTypes.length > 0 && (
                  <div className="bg-secondary/10 rounded-lg p-2 sm:p-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Selected:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableBusinessTypes
                        .filter((type) => selectedBusinessTypes.includes(type.id))
                        .map((type) => (
                          <span
                            key={type.id}
                            className="px-2 sm:px-3 py-1 bg-secondary text-white text-xs rounded-full shadow-sm"
                          >
                            {type.name}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="border-2 border-dashed border-gray-200 p-6 sm:p-8 rounded-xl text-center">
                <FaTag className="mx-auto text-gray-300 text-2xl sm:text-3xl mb-3" />
                <div className="text-xs sm:text-sm text-gray-500">
                  {selectedCategories.length === 0
                    ? "Please select categories first"
                    : "No types available"}
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2 sm:pt-4">
            <Button
              className="w-full h-11 sm:h-12 lg:h-14 text-xs sm:text-sm lg:text-base font-semibold bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 transform hover:scale-100 sm:hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              type="submit"
              isDisabled={businessMutation.isPending}
            >
              {businessMutation.isPending ? (
                <div className="flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin text-sm sm:text-base" />
                  <span className="hidden sm:inline">Creating...</span>
                  <span className="sm:hidden">Creating</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <FaStore className="text-sm sm:text-base flex-shrink-0" />
                  <span className="hidden sm:inline">Create Business</span>
                  <span className="sm:hidden">Create</span>
                  <HiSparkles className="text-sm sm:text-base hidden sm:inline flex-shrink-0" />
                </div>
              )}
            </Button>
          </div>
        </form>
      </div>
    </CustomModal>
  );
};

export default AddBusinessModal;
